import React, { useState, useEffect } from "react";
import styled from "styled-components";
import searchIcon from "../../assets/search.svg";
import { getLastMonthRange } from "../../utils";

type Preset = "last7" | "last30" | "month" | "lastMonth" | "quarter";

const PRESETS: { key: Preset; label: string }[] = [
  { key: "last7", label: "Last 7d" },
  { key: "last30", label: "Last 30d" },
  { key: "month", label: "This month" },
  { key: "lastMonth", label: "Last month" },
  { key: "quarter", label: "This quarter" },
];

const toLocalDT = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const DatePickerModal = ({
  datePickerOpen,
  onClose,
  onApply,
  initialStart = "",
  initialEnd = "",
}) => {
  const [startDate, setStartDate] = useState(initialStart);
  const [endDate, setEndDate] = useState(initialEnd);
  const [error, setError] = useState("");
  const [activePreset, setActivePreset] = useState<Preset | null>(null);

  const applyPreset = (preset: Preset) => {
    const now = new Date();
    let s: Date;
    let e = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59);
    if (preset === "last7") {
      s = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      s.setHours(0, 0, 0, 0);
      e = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59);
    } else if (preset === "last30") {
      s = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      s.setHours(0, 0, 0, 0);
    } else if (preset === "month") {
      s = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0);
    } else if (preset === "lastMonth") {
      const range = getLastMonthRange();
      s = range.start;
      e = new Date(
        range.end.getFullYear(),
        range.end.getMonth(),
        range.end.getDate(),
        23,
        59,
      );
    } else {
      s = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1, 0, 0);
    }
    setActivePreset(preset);
    onApply(toLocalDT(s), toLocalDT(e));
    onClose();
  };

  // Every time the modal opens: seed with current values from outside and clear error
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (datePickerOpen) {
      setStartDate(initialStart);
      setEndDate(initialEnd);
      setError("");
      setActivePreset(null);
    }
  }, [datePickerOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!datePickerOpen) return null;

  const handleApply = () => {
    setError("");

    if (!startDate || !endDate) {
      setError("Please select both a start date and an end date.");
      return;
    }

    if (new Date(endDate) <= new Date(startDate)) {
      setError("End date must be after the start date.");
      return;
    }

    if (new Date(endDate) < new Date(startDate)) {
      alert("End date cannot be earlier than start date!");
      return;
    }

    onApply(startDate, endDate);
    onClose();
  };

  return (
    <ModalBackdrop>
      <ModalContent>
        <h3>Set time stamp</h3>
        <PresetRow>
          {PRESETS.map((p) => (
            <PresetBtn
              key={p.key}
              active={activePreset === p.key}
              onClick={() => applyPreset(p.key)}
            >
              {p.label}
            </PresetBtn>
          ))}
        </PresetRow>
        <StyledDate>
          <label>
            <strong style={{ fontFamily: 'var(--font-label)' }}>Start Date:</strong>
            <input
              type="datetime-local"
              value={startDate}
              max="9999-12-31T23:59"
              onChange={(e) => {
                const val = e.target.value;
                const year = val.split("-")[0];
                if (year.length <= 4) {
                  setStartDate(val);
                  setActivePreset(null);
                  setError("");
                }
              }}
            />
          </label>
          <label>
            <strong style={{ fontFamily: 'var(--font-label)' }}>End Date:</strong>
            <input
              type="datetime-local"
              value={endDate}
              max="9999-12-31T23:59"
              onChange={(e) => {
                const val = e.target.value;
                const year = val.split("-")[0];
                if (year.length <= 4) {
                  setEndDate(val);
                  setActivePreset(null);
                  setError("");
                }
              }}
            />
          </label>
          {error && <ErrorText>{error}</ErrorText>}
        </StyledDate>
        <ButtonContainer>
          <CancelButton onClick={onClose}>Cancel</CancelButton>
          <SearchButton onClick={handleApply}>
            <img src={searchIcon} alt="Search" /> Search
          </SearchButton>
        </ButtonContainer>
      </ModalContent>
    </ModalBackdrop>
  );
};

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  width: 400px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const StyledDate = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 20px 0;

  label {
    display: flex;
    flex-direction: column;
    text-align: left;
    gap: 4px;
  }

  input[type="datetime-local"] {
    width: 100%;
    padding: 8px;
    border: 1px solid #ccc;
    border-radius: 4px;
    outline: none;
    box-sizing: border-box;

    &:focus {
      border-color: #888;
      box-shadow: 0 0 4px rgba(0, 0, 0, 0.1);
    }
  }
`;

const ErrorText = styled.p`
  color: #dc2626;
  font-size: 12px;
  margin: 4px 0 0;
  text-align: left;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
`;

const CancelButton = styled.button`
  background: white;
  color: var(--Primary-Color-Shade-1, #0d6ab3);
  border: 1px solid var(--Primary-Color-Shade-1, #0d6ab3);
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;

  &:hover {
    background: var(--Primary-Color-Shade-1-pale, #e6f0ff);
  }
`;

const SearchButton = styled.button`
  background: var(--Primary-Color-Shade-1, #0d6ab3);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;

  img {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: color-mix(in srgb, var(--Primary-Color-Shade-1) 80%, black);
  }
`;

const PresetRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
  margin-bottom: 4px;
`;

const PresetBtn = styled.button<{ active?: boolean }>`
  font-family: "Montserrat", sans-serif;
  font-size: 10px;
  font-weight: 700;
  color: ${({ active }) => (active ? "var(--Primary-Color-Shade-1, #0d6ab3)" : "#64748b")};
  background: ${({ active }) => (active ? "rgba(var(--Primary-Color-Shade-1-rgb), 0.10)" : "#ffffff")};
  border: 1px solid ${({ active }) => (active ? "rgba(var(--Primary-Color-Shade-1-rgb), 0.4)" : "#e2e8f0")};
  border-radius: 20px;
  padding: 4px 12px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  &:hover {
    background: rgba(var(--Primary-Color-Shade-1-rgb), 0.07);
    border-color: rgba(var(--Primary-Color-Shade-1-rgb), 0.3);
    color: var(--Primary-Color-Shade-1, #0d6ab3);
  }
`;

export default DatePickerModal;
